import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Description as DocumentIcon,
  Preview as PreviewIcon,
  HelpOutline as HelpIcon,
  Folder as FolderIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import PromptDetailDialog, { PromptViewButton } from './PromptDetailDialog';

// eslint-disable-next-line max-lines-per-function
const ReportCompletionDisplay = ({
  totalVariables,
  completedVariables = [],
  errorVariables = [],
  insufficientDataVariables = [],
  _reportId,
  onPreviewClick
}) => {
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [promptTabValue, setPromptTabValue] = useState(0);

  const successRate = totalVariables > 0
    ? Math.round((completedVariables.length / totalVariables) * 100)
    : 0;

  const hasIssues = errorVariables.length > 0 || insufficientDataVariables.length > 0;

  // Handle opening prompt dialog
  const handleViewPrompt = (variable) => {
    setSelectedVariable(variable);
    setPromptDialogOpen(true);
    setPromptTabValue(0);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      {/* Success Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CompletedIcon sx={{ fontSize: 48, color: 'success.main', mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="h3" gutterBottom>
            Report Generation Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            All {totalVariables} variables have been processed successfully
          </Typography>
        </Box>
      </Box>
      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 3
          }}>
          <Card sx={{ backgroundColor: '#e8f5e9', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CompletedIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {completedVariables.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {errorVariables.length > 0 && (
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <Card sx={{ backgroundColor: '#ffebee', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ErrorIcon sx={{ color: 'error.main', fontSize: 32, mb: 1 }} />
                <Typography variant="h4" color="error.main">
                  {errorVariables.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {insufficientDataVariables.length > 0 && (
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <Card sx={{ backgroundColor: '#fff3e0', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <WarningIcon sx={{ color: 'warning.main', fontSize: 32, mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {insufficientDataVariables.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No Data
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid
          size={{
            xs: 12,
            sm: 3
          }}>
          <Card sx={{ backgroundColor: '#f5f5f5', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReportIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
              <Typography variant="h4" color="primary.main">
                {successRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Success Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Issues Section */}
      {hasIssues && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Some variables encountered issues:
          </Typography>
          {errorVariables.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Failed Variables ({errorVariables.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {errorVariables.map((v, index) => (
                  <Chip
                    key={index}
                    label={v.variableName}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          {insufficientDataVariables.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Insufficient Data ({insufficientDataVariables.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {insufficientDataVariables.map((v, index) => (
                  <Chip
                    key={index}
                    label={v.variableName}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Alert>
      )}
      {/* Preview Button */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<PreviewIcon />}
          onClick={onPreviewClick}
          sx={{
            py: 1.5,
            px: 4,
            fontSize: '1.1rem',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-1px)'
            }
          }}
        >
          Preview Report
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {/* Next Steps */}
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <HelpIcon sx={{ mr: 1 }} />
        Next Steps
      </Typography>
      <List dense>
        <ListItem>
          <ListItemIcon>
            <DocumentIcon />
          </ListItemIcon>
          <ListItemText
            primary="Preview Your Report"
            secondary="Click the button above to see your compiled report with all processed variables"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <FolderIcon />
          </ListItemIcon>
          <ListItemText
            primary="Access in Documents"
            secondary="Your report has been automatically saved to your Documents section"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <HelpIcon />
          </ListItemIcon>
          <ListItemText
            primary="Need Help?"
            secondary="Ask me any questions about the report, variables, or request modifications"
          />
        </ListItem>
      </List>
      {/* Processed Variables Sample */}
      {completedVariables.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" gutterBottom>
            Processed Variables Sample
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {completedVariables.slice(0, 5).map((variable, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                      {variable.variableName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {variable.result || 'Processed successfully'}
                    </Typography>
                  </Box>
                  {(variable.systemPrompt || variable.userPrompt || variable.completePrompt) && (
                    <PromptViewButton variable={variable} onViewPrompt={handleViewPrompt} />
                  )}
                </Box>
              </Card>
            ))}
            {completedVariables.length > 5 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                ...and {completedVariables.length - 5} more variables
              </Typography>
            )}
          </Box>
        </>
      )}
      <PromptDetailDialog
        open={promptDialogOpen}
        onClose={() => {
          setPromptDialogOpen(false);
          setSelectedVariable(null);
          setPromptTabValue(0);
        }}
        selectedVariable={selectedVariable}
        tabValue={promptTabValue}
        onTabChange={setPromptTabValue}
      />
    </Paper>
  );
};

export default ReportCompletionDisplay;